import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import parse from 'html-react-parser';
import moment from 'moment';
import {
  animationScrollToTop,
  convertHtmlToString,
  convertMessageToHtmlAndTrimSpaces,
  getSenderName,
  getQuillTextLength,
  isJustNowMessageCheck,
  shortenFilename
} from '../../utils';
import { ReactComponent as DownloadIcon } from 'assets/LoadCircle.svg';
import { ReactComponent as AttachIcon } from 'assets/iconButtons/attach-doc-new.svg';
import { ReactComponent as SendIcon } from 'assets/iconButtons/send-button-new.svg';
import { ReactComponent as RemoveFileIcon } from 'assets/iconButtons/remove-file.svg';
import i18nContext from 'components/i18n-context';
import { MAX_MESSAGE_LENGTH } from 'components/common/constants';
import Loader from 'components/common/Loader';
import { messageRegExp, prepareFieldErrors, getErrorMessageForAlert } from 'services/utils';
import Input from 'uikit/Input/Input';
import Alert from 'uikit/Alert/Alert';
import './Chat.scss';

const Chat = ({
  topic,
  messages,
  userAccount,
  isLoading,
  isLoadingOlderMessages,
  isFileUploading,
  error,
  clearError,
  generateSecurityCode,
  setNewMessageData,
  uploadedFiles,
  uploadDocuments,
  removeAttachment,
  getCurrentTopicMessagesFilesUrls,
  downloadAttachment,
  topicsRef,
  canWriteMessages,
  clearUploadedFiles,
  isNewMessageCreated,
  setIsNewMessageCreated,
  isShowingInternalActionConfirmation,
  loadPreviousMessages,
  hasMoreMessages
}) => {
  const i18n = useContext(i18nContext);
  const { id } = useParams();
  const serverFieldsErrors = prepareFieldErrors(i18n, error);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const chatRef = useRef(null);
  const [messageValue, setMessageValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);
  const [hoveredFileId, setHoveredFileId] = useState(null);
  const previousIdRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const createdUrlsRef = useRef(new Map());
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const previousTopicIdRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const hasTriedLoadingRef = useRef(false);

  // Function to format date and time
  const formatMessageDateTime = (dateStr) => {
    const date = moment(dateStr);
    const formattedDate = date.format('DD.MM.YY');
    const formattedTime = date.format('HH:mm');
    return { date: formattedDate, time: `${formattedTime} GB` };
  };

  // Function to get day label for date separators
  const getDayLabel = (dateStr) => {
    const messageDate = moment(dateStr).startOf('day');
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const currentYear = moment().year();
    const messageYear = messageDate.year();

    if (messageDate.isSame(today)) {
      return i18n.getMessage('messages.date.today');
    } else if (messageDate.isSame(yesterday)) {
      return i18n.getMessage('messages.date.yesterday');
    } else {
      // Format as "Month Day" or "Month Day, Year"
      const monthDay = messageDate.format('MMMM D');
      if (messageYear === currentYear) {
        return monthDay; // E.g.: "December 10"
      } else {
        return `${monthDay}, ${messageYear}`; // E.g.: "December 10, 2024"
      }
    }
  };

  // Function to check if date separator should be shown
  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true; // First message always shows date

    const currentDate = moment(currentMessage.created_at || currentMessage.create_at).startOf('day');
    const previousDate = moment(previousMessage.created_at || previousMessage.create_at).startOf('day');

    return !currentDate.isSame(previousDate);
  };

  useEffect(() => {
    getCurrentTopicMessagesFilesUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Reset state when chat changes
  useEffect(() => {
    if (id && id !== previousIdRef.current) {
      previousIdRef.current = id;
      hasScrolledRef.current = false;
      setIsInitialLoad(true); // Reset initial load flag when topic changes
      hasTriedLoadingRef.current = false; // Reset loading attempt flag when topic changes
    }
  }, [id]);

  // Scroll when messages are loaded (only on initial load)
  useEffect(() => {
    // Only scroll on initial load of a topic, not when loading older messages
    if (messages.length > 0 && !isLoading && isInitialLoad && id) {
      hasScrolledRef.current = true;
      previousTopicIdRef.current = id;

      const timer = setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollTop = chatRef.current.scrollHeight;
          setIsInitialLoad(false); // Mark initial load as done
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [messages.length, isLoading, id, isInitialLoad]);

  // Handle scroll for loading previous messages
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight } = e.target;
      const scrollThreshold = 50; // Start loading when within 50px from top
      const resetThreshold = 200; // Reset loading attempt when scrolled 200px from top

      // Reset loading attempt flag when user scrolls away from top
      if (scrollTop > resetThreshold && hasTriedLoadingRef.current) {
        hasTriedLoadingRef.current = false;
      }

      // If scrolled near top and has more messages to load and haven't tried loading at this position
      if (
        scrollTop <= scrollThreshold &&
        hasMoreMessages &&
        !isLoadingPrevious &&
        !isLoadingOlderMessages &&
        !hasTriedLoadingRef.current
      ) {
        setIsLoadingPrevious(true);
        hasTriedLoadingRef.current = true; // Mark that we've attempted loading

        // Save current scroll height to calculate difference later
        const previousScrollHeight = scrollHeight;

        loadPreviousMessages()
          .then(() => {
            // Use MutationObserver to maintain scroll position immediately when DOM changes
            if (chatRef.current) {
              const observer = new MutationObserver(() => {
                const newScrollHeight = chatRef.current.scrollHeight;
                const heightDifference = newScrollHeight - previousScrollHeight;

                // Set scroll position immediately to maintain view of messages
                chatRef.current.scrollTop = heightDifference;

                // Disconnect observer after adjustment
                observer.disconnect();
              });

              // Start observing for changes in the messages container
              observer.observe(chatRef.current, {
                childList: true,
                subtree: true
              });

              // Fallback: disconnect observer after a short timeout if no changes detected
              setTimeout(() => {
                observer.disconnect();
              }, 500);
            }
          })
          .finally(() => {
            setIsLoadingPrevious(false);
          });
      }
    },
    [hasMoreMessages, isLoadingPrevious, isLoadingOlderMessages, loadPreviousMessages]
  );

  // Attach scroll listener
  useEffect(() => {
    const chatElement = chatRef.current;
    if (chatElement) {
      chatElement.addEventListener('scroll', handleScroll);
      return () => chatElement.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const form = useFormik({
    validateOnChange: false,
    initialValues: {
      message: ''
    },
    validationSchema: Yup.object({
      message: Yup.string()
        .required(i18n.getMessage('messages.error.message.empty'))
        .max(MAX_MESSAGE_LENGTH, i18n.getMessage('messages.error.message.maxSize', { size: MAX_MESSAGE_LENGTH }))
        .matches(messageRegExp, i18n.getMessage('messages.error.message.invalidFormat'))
    }),
    onSubmit: () => {
      setNewMessageData(convertMessageToHtmlAndTrimSpaces(messageValue));
      generateSecurityCode(topic.id);
    }
  });

  const { values, errors, handleSubmit, validateField, submitCount, setFieldValue, resetForm } = form;

  useEffect(() => {
    if (id) {
      resetForm();
      setMessageValue('');
      clearUploadedFiles();
      // Clean up all cached URLs when switching chats
      createdUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      });
      createdUrlsRef.current.clear();
    }
  }, [id]);

  useEffect(() => {
    setFieldValue('message', convertHtmlToString(messageValue));
  }, [messageValue, setFieldValue]);

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const onBlurMessageField = () => {
    validateField('message');
    if (serverFieldsErrors?.message) {
      clearError();
    }
  };

  // File handling functions
  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = (e) => {
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes
    const acceptedFormats = ['.pdf', '.jpg', '.jpeg', '.png'];
    const selectedFiles = Array.from(e.target.files);

    const oversizedFiles = [];
    const invalidFormatFiles = [];
    const validFiles = [];

    selectedFiles.forEach((file) => {
      const extension = `.${file.name.split('.').pop().toLowerCase()}`;

      if (!acceptedFormats.includes(extension)) {
        invalidFormatFiles.push(file);
      } else if (file.size > maxSize) {
        oversizedFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    // Show error for oversized files
    if (oversizedFiles.length > 0) {
      const errorMsg = `File size exceeds 15MB. Please select a smaller file.`;
      setFileError(errorMsg);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Show error for invalid format files
    if (invalidFormatFiles.length > 0) {
      const errorMsg = `Invalid file format. Allowed formats: .pdf, .jpg, .jpeg, .png`;
      setFileError(errorMsg);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Clear errors if valid files are being processed
    setFileError(null);

    if (validFiles.length > 0) {
      uploadDocuments(e);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const maxSize = 15 * 1024 * 1024; // 15MB in bytes
      const acceptedFormats = ['.pdf', '.jpg', '.jpeg', '.png'];
      const droppedFiles = Array.from(e.dataTransfer.files);

      const oversizedFiles = [];
      const invalidFormatFiles = [];
      const validFiles = [];

      droppedFiles.forEach((file) => {
        const extension = `.${file.name.split('.').pop().toLowerCase()}`;

        if (!acceptedFormats.includes(extension)) {
          invalidFormatFiles.push(file);
        } else if (file.size > maxSize) {
          oversizedFiles.push(file);
        } else {
          validFiles.push(file);
        }
      });

      // Show error for oversized files
      if (oversizedFiles.length > 0) {
        const errorMsg = `File size exceeds 15MB. Please select a smaller file.`;
        setFileError(errorMsg);
        return;
      }

      // Show error for invalid format files
      if (invalidFormatFiles.length > 0) {
        const errorMsg = `Invalid file format. Allowed formats: .pdf, .jpg, .jpeg, .png`;
        setFileError(errorMsg);
        return;
      }

      // Clear errors if valid files are being processed
      setFileError(null);

      if (validFiles.length > 0) {
        const fakeEvent = {
          target: {
            files: validFiles
          }
        };
        uploadDocuments(fakeEvent);
      }
    }
  };

  // Make entire textarea clickable
  const handleTextAreaClick = (e) => {
    // Prevent click from bubbling when actually typing in editor
    if (e.target.classList.contains('ql-editor')) {
      return;
    }

    // Don't focus if clicking on toolbar buttons or tooltip
    if (e.target.closest('.ql-toolbar') || e.target.closest('.ql-tooltip')) {
      return;
    }

    // Focus the quill editor
    const quillEditor = e.currentTarget.querySelector('.ql-editor');
    if (quillEditor) {
      quillEditor.focus();
    }
  };

  // Helper functions for file preview
  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '0 KB';

    // Handle if bytes is passed as a string
    const size = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;

    if (!size || isNaN(size)) return '0 KB';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const truncateFileName = (name, maxLength = 8) => {
    if (!name) return '';
    const extension = name.split('.').pop();
    const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
    if (name.length <= 6) return name;
    const truncatedName =
      nameWithoutExt.substring(0, maxLength) + '...' + nameWithoutExt.substring(nameWithoutExt.length - 2);
    return `${truncatedName}.${extension}`;
  };

  const createAndTrackUrl = (file, fileId) => {
    // Check if we already have a URL for this file
    if (createdUrlsRef.current.has(fileId)) {
      return createdUrlsRef.current.get(fileId);
    }

    const url = URL.createObjectURL(file);
    createdUrlsRef.current.set(fileId, url);
    return url;
  };

  const getFilePreview = (file) => {
    // Check if file is an image by type or extension
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const pdfExtensions = ['pdf'];
    const extension = file.name ? file.name.split('.').pop().toLowerCase() : '';

    // Use originalFile if it exists (for uploaded files from server)
    const fileToPreview = file.originalFile || file;

    const isImage =
      (fileToPreview.type && fileToPreview.type.startsWith('image/')) ||
      (file.type && file.type.startsWith('image/')) ||
      imageExtensions.includes(extension);

    const isPdf =
      (fileToPreview.type && fileToPreview.type === 'application/pdf') ||
      (file.type && file.type === 'application/pdf') ||
      pdfExtensions.includes(extension);

    if (isImage) {
      // If we have an originalFile property, use it
      if (file.originalFile && file.originalFile instanceof File) {
        return { type: 'image', url: createAndTrackUrl(file.originalFile, file.id) };
      }
      // If file has a URL property (already uploaded), use it
      if (file.url) {
        return { type: 'image', url: file.url };
      }
      // If it's a File object, create a blob URL
      if (file instanceof File) {
        return { type: 'image', url: createAndTrackUrl(file, file.id || file.name) };
      }
      // If file has a preview property
      if (file.preview) {
        return { type: 'image', url: file.preview };
      }
      // Try to use the file directly if it's a Blob
      if (file instanceof Blob) {
        return { type: 'image', url: createAndTrackUrl(file, file.id || 'blob') };
      }
    }

    if (isPdf) {
      // If we have an originalFile property, use it
      if (file.originalFile && file.originalFile instanceof File) {
        return { type: 'pdf', url: createAndTrackUrl(file.originalFile, file.id) };
      }
      // If file has a URL property (already uploaded), use it
      if (file.url) {
        return { type: 'pdf', url: file.url };
      }
      // If it's a File object, create a blob URL
      if (file instanceof File) {
        return { type: 'pdf', url: createAndTrackUrl(file, file.id || file.name) };
      }
      // If file has a preview property
      if (file.preview) {
        return { type: 'pdf', url: file.preview };
      }
      // Try to use the file directly if it's a Blob
      if (file instanceof Blob) {
        return { type: 'pdf', url: createAndTrackUrl(file, file.id || 'blob') };
      }
      // For PDFs without URL, just return type indicator
      return { type: 'pdf' };
    }

    return { type: 'other', extension };
  };

  // Clear file error after 5 seconds
  useEffect(() => {
    if (fileError) {
      const timer = setTimeout(() => {
        setFileError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [fileError]);

  // Cleanup created URLs on unmount
  useEffect(() => {
    return () => {
      createdUrlsRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors when revoking URLs
        }
      });
    };
  }, []);

  // Helper function to handle file removal
  const handleRemoveFile = (fileId) => {
    // Clean up the cached URL for this file
    if (createdUrlsRef.current.has(fileId)) {
      const url = createdUrlsRef.current.get(fileId);
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors
      }
      createdUrlsRef.current.delete(fileId);
    }
    // Call the original removeAttachment
    removeAttachment(fileId);
  };

  useEffect(() => {
    if (isNewMessageCreated) {
      // Scroll to bottom instantly when a new message is created
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
      animationScrollToTop(topicsRef);
      setHoveredIndex(null);
      setFieldValue('message', '');
      setMessageValue('');
      resetForm();
      setIsNewMessageCreated(false);
    }
  }, [isNewMessageCreated]);

  return (
    <div className='chat-wrapper'>
      {isLoading ? (
        <div className='loader-container'>
          <Loader />
        </div>
      ) : (
        <>
          <div className='chat-messages-container' ref={chatRef}>
            {/* Loader for loading older messages */}
            {(isLoadingOlderMessages || isLoadingPrevious) && (
              <div className='loader-container-top'>
                <Loader />
              </div>
            )}
            {messages.map((message, index) => {
              const isJustNowMessageCreated = isJustNowMessageCheck(message.created_at || message.create_at);
              const isOperator = message?.sender_type === 'OPERATOR';
              const showDateSeparator = shouldShowDateSeparator(message, messages[index - 1]);
              const dateLabel = getDayLabel(message.created_at || message.create_at);

              return (
                <React.Fragment key={index}>
                  {showDateSeparator && (
                    <div className='date-separator'>
                      <span className='date-separator-text'>{dateLabel}</span>
                    </div>
                  )}
                  <div
                    className={`chat-message ${isOperator ? 'message-operator' : 'message-user'}`}
                    data-message-id={message.id || `message-${index}`}
                  >
                    <div className='message-bubble'>
                      <div className='message-header'>
                        <span className='message-author'>{getSenderName(message?.sender_type, userAccount)}</span>
                        {isJustNowMessageCreated ? (
                          <div className='message-date'>
                            <span className='message-date-just-now'>{i18n.getMessage('messages.date.justNow')}</span>
                          </div>
                        ) : (
                          <div className='message-date'>
                            <span className='message-date-text'>
                              {formatMessageDateTime(message.created_at || message.create_at).date}
                            </span>
                            <span className='message-date-text'>
                              {formatMessageDateTime(message.created_at || message.create_at).time}
                            </span>
                          </div>
                        )}
                      </div>
                      <div
                        className='message-body'
                        ref={(el) => {
                          if (el) {
                            // Mark empty paragraphs for CSS styling
                            const paragraphs = el.querySelectorAll('p');
                            paragraphs.forEach((p) => {
                              // Check if paragraph is effectively empty (only br or whitespace)
                              const hasOnlyBr = p.children.length === 1 && p.children[0].tagName === 'BR';
                              const isEmpty = !p.textContent.trim() || hasOnlyBr;

                              if (isEmpty) {
                                p.setAttribute('data-empty', 'true');
                                // Also set line-height directly if CSS doesn't work
                                p.style.lineHeight = '1';
                                p.style.height = '1em';
                              }
                            });
                          }
                        }}
                      >
                        {message.body ? parse(message.body) : ''}
                      </div>
                      {message.documents.length > 0 && (
                        <div className='message-attachments'>
                          <span className='attachments-label'>
                            {i18n.getMessage('messages.attachments.label')} ({message.documents.length})
                          </span>
                          <div className='attachments-list'>
                            {message.documents.map((file, fileIndex) => {
                              const hoveredKey = `${index}-${fileIndex}`;
                              return (
                                <div key={fileIndex} className='attachment-item'>
                                  <div
                                    className='attachment-preview-container'
                                    onMouseEnter={() => handleMouseEnter(hoveredKey)}
                                    onMouseLeave={handleMouseLeave}
                                  >
                                    {!file.url ? (
                                      <div className='attachment-preview'>
                                        <p className='placeholder-wave h-100'>
                                          <span className='placeholder w-100 h-100 skeleton' />
                                        </p>
                                      </div>
                                    ) : (file.contentType || file.content_type) === 'application/pdf' ? (
                                      <embed className='attachment-preview' src={file.url} type='application/pdf' />
                                    ) : (
                                      <img className='attachment-preview' src={file.url} alt={file.name} />
                                    )}
                                    <div className='attachment-overlay'>
                                      {hoveredIndex === hoveredKey && (
                                        <>
                                          <div className='overlay-top'>
                                            <span className='attachment-name'>{shortenFilename(file.name)}</span>
                                          </div>
                                          <div className='overlay-bottom'>
                                            <button
                                              className='attachment-download-btn'
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                downloadAttachment(file.id);
                                              }}
                                              title={i18n.getMessage('messages.attachments.download')}
                                            >
                                              <DownloadIcon />
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className='attachment-filename'>
                                    <span title={file.name}>{file.name}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {canWriteMessages && (
            <div className='chat-input-container'>
              <form action='#' id='chat-form' onSubmit={handleSubmit} className='chat-form'>
                <div className='chat-input-area'>
                  <div
                    className={`chat-input-wrapper ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type='file'
                      multiple
                      accept='.pdf,.jpg,.jpeg,.png'
                      onChange={handleFileSelect}
                      disabled={isFileUploading}
                      style={{ display: 'none' }}
                    />

                    <button
                      type='button'
                      className='attach-button'
                      onClick={handleAttachClick}
                      disabled={isFileUploading}
                      title={i18n.getMessage('messages.button.attachFile')}
                    >
                      <AttachIcon />
                    </button>

                    <div
                      className='text-input-area'
                      onClick={handleTextAreaClick}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleTextAreaClick(e);
                        }
                      }}
                      role='button'
                      tabIndex={0}
                    >
                      <Input
                        className='chat-form-input'
                        type={'text-editor-quill'}
                        autoFocus={true}
                        placeholder={'Enter your message...'}
                        name={'message'}
                        isDisabled={isShowingInternalActionConfirmation}
                        value={messageValue}
                        onChange={setMessageValue}
                        onBlur={onBlurMessageField}
                        initialStatus={submitCount}
                        max={MAX_MESSAGE_LENGTH}
                        rows={4}
                        error={errors?.message || serverFieldsErrors?.message}
                        /* eslint-disable-next-line max-len */
                        symbolsCountText={i18n.getMessage('messages.form.input.message.symbolsLeft', {
                          amount: MAX_MESSAGE_LENGTH - getQuillTextLength(values.message)
                        })}
                      />
                    </div>

                    <button
                      type='submit'
                      className='send-button'
                      disabled={isFileUploading || !messageValue.trim()}
                      title={i18n.getMessage('messages.button.sendMessage')}
                    >
                      {isFileUploading ? <Loader isText={false} /> : <SendIcon />}
                    </button>
                  </div>

                  {/* File error message */}
                  {fileError && <div className='file-error-message'>{fileError}</div>}

                  {/* General error message */}
                  {error && !serverFieldsErrors?.message && (
                    <Alert
                      type={'warning'}
                      className={'chat-general-error-alert'}
                      message={getErrorMessageForAlert(i18n, error)}
                    />
                  )}

                  {/* Files preview below the input */}
                  {uploadedFiles && uploadedFiles.length > 0 && (
                    <div className='files-preview-wrapper'>
                      <div className='files-preview'>
                        {uploadedFiles.map((file) => {
                          const fileId = file.id || file.name;
                          // Get preview only once and cache it
                          const preview = getFilePreview(file);
                          return (
                            <div
                              key={fileId}
                              className='file-card'
                              onMouseEnter={() => setHoveredFileId(fileId)}
                              onMouseLeave={() => setHoveredFileId(null)}
                            >
                              {!preview || !preview.url ? (
                                <div className='file-preview-placeholder'>
                                  <p className='placeholder-wave h-100'>
                                    <span className='placeholder w-100 h-100 skeleton' />
                                  </p>
                                </div>
                              ) : preview.type === 'pdf' ? (
                                <embed className='file-pdf-preview' src={preview.url} type='application/pdf' />
                              ) : preview.type === 'image' ? (
                                <img src={preview.url} alt={file.name} className='file-image-background' />
                              ) : (
                                <div className='file-default-background'>
                                  <div className='file-type-icon'>{preview.extension?.toUpperCase()}</div>
                                </div>
                              )}

                              <div className={`file-overlay ${hoveredFileId === fileId ? 'hovered' : ''}`}>
                                {hoveredFileId === fileId && (
                                  <>
                                    <div className='file-info'>
                                      <span className='file-name'>
                                        {truncateFileName(file.name || 'Unnamed', 4)} (
                                        {formatFileSize(file.size || file.originalFile?.size || 0)})
                                      </span>
                                    </div>
                                    <div className='file-actions'>
                                      <button
                                        className='file-action-btn delete-btn'
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveFile(file.id || fileId);
                                        }}
                                        title='Delete file'
                                        type='button'
                                      >
                                        <RemoveFileIcon />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

Chat.propTypes = {
  isShowingInternalActionConfirmation: PropTypes.bool.isRequired,
  topic: PropTypes.object,
  messages: PropTypes.array.isRequired,
  userAccount: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isFileUploading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  clearError: PropTypes.func.isRequired,
  generateSecurityCode: PropTypes.func.isRequired,
  setNewMessageData: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  uploadDocuments: PropTypes.func.isRequired,
  removeAttachment: PropTypes.func.isRequired,
  getCurrentTopicMessagesFilesUrls: PropTypes.func.isRequired,
  downloadAttachment: PropTypes.func.isRequired,
  topicsRef: PropTypes.any.isRequired,
  canWriteMessages: PropTypes.bool.isRequired,
  clearUploadedFiles: PropTypes.func.isRequired,
  isNewMessageCreated: PropTypes.bool.isRequired,
  setIsNewMessageCreated: PropTypes.func.isRequired
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(Chat));
